import axios from "axios";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { GroupLang, LessonData } from "../../../lib/languages";

const DialogDeleteGroups = ({ level, lesson, getAllGroups }) => {
  const { language } = useLanguage();
  const t = GroupLang[language] || GroupLang["en"];

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDeleteLesson = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.delete(
        `${BaseUrlApi}/levels/${level.id}/lessons/${lesson.bid}`
      );
      toast(t.deleteLesson, {
        description: t.deleteLessonDialogDescription,
      });
      await getAllGroups();
      setOpen(false);
    } catch (error) {
      toast(t.error, { description: ErrorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const [confirmText, setConfirmText] = useState("");
  const confirmationWord = "DELETE";
  const isConfirmEnabled = confirmText.toUpperCase() === confirmationWord;

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <DialogTrigger asChild>
        <Button
          size="icon"
          variant="destructive"
          className="rounded-xl h-9"
          onClick={() => {}}
        >
          <Trash2 />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <DialogTitle className="text-red-500">
              {t.deleteLessonDialogTitle} "{lesson.name || "---"}"
            </DialogTitle>
          </div>
          <DialogDescription>
            {t.deleteLessonDialogDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 border border-destructive/20 bg-destructive/5 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-500">
              {t.deleteWarning}
            </p>
          </div>
          <div className="pl-6">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="bg-background">
                {lesson?.users_id?.length || 0}
              </Badge>
              <span className="text-sm">{t.studentsWillBeDeleted}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t.associatedContentRemoval}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="confirm" className="text-sm font-medium">
            {t.typeToConfirm}
          </label>
          <Input
            id="confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={
              !isConfirmEnabled
                ? "border-destructive focus-visible:ring-0"
                : "border-green-500 focus-visible:ring-0"
            }
            placeholder={confirmationWord}
          />
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t.cancel}
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeleteLesson}
            disabled={loading || !isConfirmEnabled}
            className="gap-2"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {t.deleteLesson}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDeleteGroups;
